import React from 'react';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { useTheme } from '../../context/ThemeContext';

const PrivacyPolicyEn = () => {
  const { isDarkMode } = useTheme();

  const pageBg = isDarkMode ? DARK_THEME.background : '#f3f8fa';
  const ink = isDarkMode ? DARK_THEME.textPrimary : '#0F2630';
  const muted = isDarkMode ? DARK_THEME.textSecondary : '#5b6b72';
  const line = isDarkMode ? DARK_THEME.border : '#e3eaed';
  const navy = isDarkMode ? DARK_THEME.primary : '#173D4D';
  const accent = isDarkMode ? DARK_THEME.primary : COLORS.primary;

  const s = {
    page: { backgroundColor: pageBg, color: ink, lineHeight: 1.65 },
    hero: {
      paddingTop: '120px',
      paddingBottom: '40px',
      paddingLeft: '20px',
      paddingRight: '20px',
      background: isDarkMode
        ? `linear-gradient(135deg, ${DARK_THEME.backgroundSecondary} 0%, ${DARK_THEME.background} 100%)`
        : `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
      textAlign: 'center',
    },
    heroTitle: { fontSize: 'clamp(28px, 5vw, 40px)', color: COLORS.white, fontWeight: 700, margin: '0 0 8px' },
    heroMeta: { color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', margin: 0 },
    wrap: { maxWidth: '860px', margin: '0 auto', padding: '40px 20px 80px' },
    p: { fontSize: '1rem', margin: '0 0 14px' },
    h2: { fontSize: '1.3rem', margin: '36px 0 12px', color: navy, fontWeight: 700 },
    h3: { fontSize: '1.05rem', margin: '22px 0 8px', color: ink, fontWeight: 600 },
    ul: { paddingLeft: '22px', margin: '0 0 14px' },
    li: { fontSize: '1rem', marginBottom: '6px' },
    a: { color: accent, textDecoration: 'none' },
    fill: {
      backgroundColor: isDarkMode ? `${DARK_THEME.primary}1A` : '#eafafb',
      border: `1px solid ${isDarkMode ? DARK_THEME.border : '#bfe9ef'}`,
      borderRadius: '12px',
      padding: '18px 20px',
      margin: '22px 0',
      fontSize: '1rem',
      lineHeight: 1.8,
    },
    note: {
      backgroundColor: isDarkMode ? `${DARK_THEME.primary}14` : '#eafafb',
      border: `1px solid ${isDarkMode ? DARK_THEME.border : '#bfe9ef'}`,
      borderRadius: '12px',
      padding: '16px 18px',
      margin: '20px 0',
      fontSize: '1rem',
    },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', margin: '14px 0', fontSize: '0.95rem' },
    th: {
      textAlign: 'left',
      border: `1px solid ${line}`,
      padding: '10px 12px',
      backgroundColor: isDarkMode ? DARK_THEME.backgroundTertiary : '#eef4f6',
      color: ink,
      verticalAlign: 'top',
    },
    td: { textAlign: 'left', border: `1px solid ${line}`, padding: '10px 12px', color: muted, verticalAlign: 'top' },
    footer: {
      marginTop: '44px',
      paddingTop: '20px',
      borderTop: `1px solid ${line}`,
      color: muted,
      fontSize: '0.9rem',
    },
    em: { color: muted },
  };

  return (
    <div style={s.page}>
      <header style={s.hero}>
        <h1 style={s.heroTitle}>Privacy Policy</h1>
        <p style={s.heroMeta}>
          <strong>Turkic Silk Road</strong> mobile application · Last updated: 17 June 2026
        </p>
      </header>

      <div style={s.wrap}>
        <p style={s.p}>
          This Privacy Policy explains which of your personal data is processed when you use the{' '}
          <strong>Turkic Silk Road</strong> mobile application (the “App”), for what purposes and on what legal
          grounds it is processed, with whom it is shared, and the rights you have. By using the App, you accept
          this policy.
        </p>

        <div style={s.fill}>
          <strong>Data Controller:</strong> Turkic Silk Road - Organization of Turkic States<br />
          <strong>Address:</strong> Beyazıt, 34452 Fatih/İstanbul<br />
          <strong>Contact / Requests:</strong>{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>
        </div>

        <h2 style={s.h2}>1. Data We Collect</h2>

        <h3 style={s.h3}>a) Account and profile information</h3>
        <ul style={s.ul}>
          <li style={s.li}>Email address and password (authentication is performed via Google Firebase Authentication; <strong>your password is never seen by us or stored in plain text</strong>).</li>
          <li style={s.li}>Display name / full name; optional: phone, date of birth, gender, profile photo (avatar).</li>
          <li style={s.li}>Preferred language and time zone.</li>
          <li style={s.li}>If you enable two-factor authentication (2FA), the related factor information (on the Firebase side).</li>
        </ul>

        <h3 style={s.h3}>b) Location data</h3>
        <p style={s.p}>
          Location is processed only <strong>while the app is open (when-in-use)</strong> and when you use a relevant
          feature: listing nearby places, trip/route planning (TurkicRoute AI), the “I’ve been here” visit log, and
          selecting a location from the map. <strong>Location is not collected in the background.</strong> You can
          disable the permission at any time from your device settings.
        </p>

        <h3 style={s.h3}>c) Camera and photo gallery</h3>
        <p style={s.p}>
          Used only when you initiate an action (uploading a profile photo or adding a place report/contribution
          image). Your gallery or camera is not continuously monitored; only the images you select/capture are
          processed.
        </p>

        <h3 style={s.h3}>d) Device and notification data</h3>
        <ul style={s.ul}>
          <li style={s.li}>An anonymous device identifier for device-bound features.</li>
          <li style={s.li}>If you grant notification permission, a notification token (Google Firebase Cloud Messaging) so that push notifications can be sent.</li>
        </ul>

        <h3 style={s.h3}>e) Content you create</h3>
        <p style={s.p}>
          Comments and ratings, place reports/edits, routes you create or save, trip plans, visits, favorites, and
          contribution/badge data (including images and locations you optionally add).
        </p>

        <h3 style={s.h3}>f) Automatically collected technical data</h3>
        <p style={s.p}>
          Basic technical records for the operation and security of the service (IP address, request time,
          error/diagnostic logs).
        </p>

        <div style={s.note}>
          <strong>Important:</strong> The app does <strong>not use any advertising network, analytics, or tracking
          software.</strong> We do not sell or share your data for advertising purposes.
        </div>

        <h2 style={s.h2}>2. For What Purposes and on What Legal Grounds We Process Data</h2>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <tbody>
              <tr><th style={s.th}>Purpose</th><th style={s.th}>Legal ground (KVKK Art. 5 / GDPR Art. 6)</th></tr>
              <tr><td style={s.td}>Account creation, login, email verification, 2FA</td><td style={s.td}>Performance of a contract; explicit consent</td></tr>
              <tr><td style={s.td}>Storing and displaying content you save (favorites, trips, visits, contributions)</td><td style={s.td}>Performance of a contract</td></tr>
              <tr><td style={s.td}>Location, camera/gallery, notification features</td><td style={s.td}>Explicit consent (via device permissions)</td></tr>
              <tr><td style={s.td}>Security, prevention of misuse, improvement of the service</td><td style={s.td}>Legitimate interest</td></tr>
              <tr><td style={s.td}>Fulfillment of legal obligations</td><td style={s.td}>Legal obligation</td></tr>
            </tbody>
          </table>
        </div>

        <h2 style={s.h2}>3. App Permissions</h2>
        <ul style={s.ul}>
          <li style={s.li}><strong>Location (when-in-use only):</strong> nearby places, route/trip planning, visit logging, selection from the map.</li>
          <li style={s.li}><strong>Camera:</strong> taking photos for a place report or contribution image.</li>
          <li style={s.li}><strong>Photo gallery:</strong> selecting a profile photo or contribution image.</li>
          <li style={s.li}><strong>Notifications:</strong> push notifications for events, news, and updates.</li>
        </ul>
        <p style={s.p}>
          All permissions are optional and can be revoked at any time from device settings. Most of the app’s
          features (map, routes, events, news, member states) can be used without logging in and without these
          permissions.
        </p>

        <h2 style={s.h2}>4. Third Parties With Whom Data Is Shared</h2>
        <p style={s.p}>
          Your personal data is shared only with the following service providers that are necessary for providing
          the service, and only to the extent necessary:
        </p>
        <ul style={s.ul}>
          <li style={s.li}><strong>Google (Firebase Authentication &amp; Cloud Messaging):</strong> authentication, email verification, two-factor authentication, and the push notification token.</li>
          <li style={s.li}><strong>Server infrastructure:</strong> The app’s data is hosted on the servers running the service.</li>
          <li style={s.li}><strong>Map and route services:</strong> Open-source map tiles (OpenStreetMap-based / CARTO) and satellite imagery (Esri), along with route-drawing services. When you use the map, your IP address and the coordinates of the displayed area are sent to the relevant providers as a technical request.</li>
        </ul>
        <p style={s.p}>
          Apart from these, we do not sell, rent, or share your data with third parties for marketing purposes.
          Where legally required, data may be shared with authorized public authorities.
        </p>

        <h2 style={s.h2}>5. Cookies and Tracking</h2>
        <p style={s.p}>
          The mobile app does not use advertising cookies or third-party tracking technology. The identity tokens
          required to maintain your session are stored in a secure area (Secure Store) on your device.
        </p>

        <h2 style={s.h2}>6. Data Retention</h2>
        <p style={s.p}>
          Your personal data is retained for as long as your account is active and necessary for the relevant
          purpose. Legal retention obligations are reserved. When you delete your account, the section below applies.
        </p>

        <h2 style={s.h2}>7. Deleting Your Account and Data</h2>
        <p style={s.p}>
          You can remove your account from within the app via <em style={s.em}>Account → Permanently delete account</em>,
          or by requesting deletion via{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>. Upon deletion:
        </p>
        <ul style={s.ul}>
          <li style={s.li}>Your personal data (name, email, phone, date of birth, etc.) is deleted or anonymized.</li>
          <li style={s.li}>Contributions you previously published publicly (e.g., a comment/route) may be retained <strong>anonymously</strong> as “Turkic Silk Road User” for the integrity of the community.</li>
          <li style={s.li}>You can additionally clear device-bound data from within the app.</li>
        </ul>

        <h2 style={s.h2}>8. Data Security</h2>
        <p style={s.p}>
          Data is encrypted in transit with HTTPS/TLS; session tokens are kept in a secure storage area on the
          device; passwords are stored hashed on the identity provider (Firebase) side. Although no method is 100%
          secure, we apply reasonable technical and administrative measures to protect your data.
        </p>

        <h2 style={s.h2}>9. International Data Transfer</h2>
        <p style={s.p}>
          Authentication and notification services (Google/Firebase) and some infrastructure components may be
          operated by providers whose servers are located abroad. These transfers are carried out in accordance with
          the applicable legislation (KVKK / GDPR).
        </p>

        <h2 style={s.h2}>10. Children’s Privacy</h2>
        <p style={s.p}>
          The app is not intended for children under the age of 13 (16 in some countries), and we do not knowingly
          collect personal data from children. If we become aware that we have processed a child’s data, we will
          delete it.
        </p>

        <h2 style={s.h2}>11. Your Rights</h2>
        <p style={s.p}>
          Under the KVKK (Law No. 6698) Art. 11 and the GDPR, you have the right to learn whether your personal data
          is being processed, to request information, to have it corrected, to request its deletion/destruction, to
          restrict or object to processing, to data portability, and to withdraw the consent you have given. You can
          submit your requests to{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>; your application
          will be answered within the legal time limits.
        </p>

        <h2 style={s.h2}>12. Changes to This Policy</h2>
        <p style={s.p}>
          We may update this policy from time to time. We will announce significant changes within the app or on
          this page; the “Last updated” date at the top of the page indicates the current version.
        </p>

        <h2 style={s.h2}>13. Contact</h2>
        <p style={s.p}>
          For all questions and requests regarding privacy:{' '}
          <a style={s.a} href="mailto:iletisim@turkicsilkroad.com">iletisim@turkicsilkroad.com</a>
        </p>

        <footer style={s.footer}>
          © 2026 Turkic Silk Road. This document is for informational purposes.
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicyEn;
